import { Box, Paper, Skeleton, Stack } from "@mui/material";

/**
 * Loading placeholders that mirror the shape of what's coming — the enterprise alternative to a
 * centred spinner. Use these instead of <CircularProgress> for page/list/detail loads.
 */

/** A table/list placeholder: a header strip + N shimmering rows inside a card. */
export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Box sx={{ px: 2, py: 1.5, bgcolor: "action.hover" }}>
        <Skeleton variant="text" width="18%" height={16} />
      </Box>
      <Box sx={{ p: 2 }}>
        {Array.from({ length: rows }).map((_, r) => (
          <Stack key={r} direction="row" spacing={2} sx={{ py: 1 }}>
            {Array.from({ length: columns }).map((_, c) => (
              <Skeleton key={c} variant="text" height={20}
                sx={{ flex: c === 0 ? "0 0 12%" : c === columns - 1 ? "0 0 10%" : 1 }} />
            ))}
          </Stack>
        ))}
      </Box>
    </Paper>
  );
}

/** A row of KPI-tile placeholders. */
export function StatsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
      {Array.from({ length: count }).map((_, i) => (
        <Paper key={i} variant="outlined" sx={{ flex: "1 1 180px", minWidth: 160, p: 2 }}>
          <Skeleton variant="text" width="60%" height={14} />
          <Skeleton variant="text" width="40%" height={34} sx={{ mt: 0.5 }} />
        </Paper>
      ))}
    </Stack>
  );
}

/** A detail/form placeholder: title + a grid of field lines inside a card. */
export function DetailSkeleton({ lines = 6 }: { lines?: number }) {
  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Skeleton variant="text" width="30%" height={28} />
      <Box
        sx={{
          mt: 2, display: "grid", gap: 2,
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        }}
      >
        {Array.from({ length: lines }).map((_, i) => (
          <Box key={i}>
            <Skeleton variant="text" width="40%" height={12} />
            <Skeleton variant="text" width="75%" height={22} />
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
